import {Button} from '@chekinapp/ui';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Counter, CounterSize} from 'components/common/Counter';
import {Fragment} from 'react/jsx-runtime';
import {Separator} from '@chekin/guestapp-ui-kit';
import {LoaderCircle, ShoppingCart} from 'lucide-react';
import {getPrice, getTotalPrice, hasItems} from './utils';
import styles from './styles.module.css';
import cn from 'clsx';
import {EsimPackage} from '@chekin/core';

type Props = {
  currencySign?: string;
  isLoading: boolean;
  className?: string;
  esimPackages: EsimPackage[];
  formId: string;
  onChange: (id: string, onChange: (value: number) => void) => (value: number) => void;
};
export const TotalToPayForm = ({
  className,
  esimPackages,
  formId,
  isLoading,
  currencySign,
  onChange,
}: Props) => {
  const {t} = useTranslation();
  const {
    control,
    formState: {isSubmitting},
  } = useFormContext();
  const values = useWatch({control});
  const isNotEmpty = hasItems(esimPackages)(values);

  return (
    <div
      className={cn(
        className,
        'flex flex-col gap-10 p-10 shadow-md rounded-2xl self-start bg-[var(--navigation-background)]',
      )}
    >
      <div className="flex flex-col gap-6">
        <div className="text-xl font-semibold">
          {t('esim.choose_plan.selected_plans')}
        </div>

        {isNotEmpty ? (
          <div className="w-full grid gap-x-2 gap-y-4 grid-cols-3 [&>*:nth-child(3n+2)]:justify-self-center [&>*:nth-child(3n+3)]:justify-self-end">
            {esimPackages.map(
              esimPackage =>
                values[esimPackage.package_id]?.checked && (
                  <Fragment key={esimPackage.package_id}>
                    <div>
                      <div className="text-sm">{esimPackage.package_data}</div>
                      <div className="text-xs text-[var(--font-secondary)]">
                        {t('esim.choose_plan.valid_for_days', {
                          days: esimPackage.package_days,
                        })}
                      </div>
                    </div>

                    <Controller
                      name={`${esimPackage.package_id}.count`}
                      control={control}
                      render={({field}) => (
                        <Counter
                          className={styles.TotalToPay__counter}
                          value={field.value ?? 1}
                          onChange={onChange(esimPackage.package_id, field.onChange)}
                          size={CounterSize.S}
                        />
                      )}
                    />

                    <div className="flex items-center gap-1">
                      {currencySign}
                      {getPrice(esimPackage, values[esimPackage.package_id]?.count)}
                    </div>
                  </Fragment>
                ),
            )}
          </div>
        ) : (
          <div className="text-sm text-[var(--font-secondary)]">
            {t('esim.choose_plan.no_items_selected')}
          </div>
        )}
        <Separator />
        <div className="flex w-full justify-between gap-2 font-semibold">
          <div>{t('esim.choose_plan.total_to_pay')}</div>
          <div className="flex items-center gap-1">
            {currencySign}
            {getTotalPrice(esimPackages)(values).toFixed(2)}
          </div>
        </div>
      </div>
      <Button
        form={formId}
        type="submit"
        disabled={!isNotEmpty || isLoading || isSubmitting}
        className="flex items-center gap-2 text-base"
      >
        {isSubmitting ? (
          <LoaderCircle size={20} className="animate-spin" />
        ) : (
          <ShoppingCart size={20} className="[--lucide-stroke-width:3]" />
        )}
        {t('esim.choose_plan.buy_now')}
      </Button>
    </div>
  );
};
